import React, { FC, useCallback, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Checkbox, Typography } from '@sravni/react-design-system';
import { useDispatch } from 'react-redux';
import { useSelection } from '../../../../../store/reducers/selection';
import { FullNameOption, FullNameSuggest } from '../../../ui/suggest/full_name';
import { FORM_FIELDS, LABELS, PAGE } from '../../../../../constants';
import { Row } from '../../common';
import { Col, TextInput } from '../../../../landing.common';
import { AgreementLink } from '../../../../agreement.link';
import { GosuslugiAuth } from '../../../../gosuslugi';
import { useUser } from '../../../../../store/reducers/user';
import { extendFormData } from '../../../../../store/reducers/formStore';
import { useIsPage } from '../../../../../store/selectors/selection';
import { PhoneForm } from './phone';

interface Props {
    form: UseFormReturn;
}

const { Heading } = Typography;

export const ContactDetailsFields: FC<Props> = (props) => {
    const { form } = props;
    const { page } = useSelection();
    const user = useUser();
    const dispatch = useDispatch();
    const { isMainPage } = useIsPage();

    const {
        control,
        formState: { errors, dirtyFields: _ },
        setValue,
        getValues,
        watch,
        trigger,
    } = form;

    const [patronymic] = watch([FORM_FIELDS.MIDDLE_NAME]);

    const onFullNameSelect = useCallback(
        async (option: FullNameOption) => {
            setValue(FORM_FIELDS.FIRST_NAME, option.firstName || '');
            setValue(FORM_FIELDS.LAST_NAME, option.lastName || '');
            setValue(FORM_FIELDS.MIDDLE_NAME, option.patronymic || null);
            setValue(FORM_FIELDS.GENDER, option.gender || '');
            await trigger([FORM_FIELDS.LAST_NAME, FORM_FIELDS.FIRST_NAME, FORM_FIELDS.MIDDLE_NAME]);
        },
        [setValue, trigger],
    );

    const onChangeNoPatronymic: React.ComponentProps<typeof Checkbox>['onChange'] = useCallback(
        async (event) => {
            if (event.currentTarget.checked) {
                const fullName = [getValues(FORM_FIELDS.LAST_NAME), getValues(FORM_FIELDS.FIRST_NAME)]
                    .filter((v) => v)
                    .join(' ');
                setValue(FORM_FIELDS.MIDDLE_NAME, null);
                setValue(FORM_FIELDS.FULL_NAME, fullName);
            } else {
                setValue(FORM_FIELDS.MIDDLE_NAME, undefined);
                dispatch(extendFormData({ [FORM_FIELDS.MIDDLE_NAME]: undefined }));
            }
            await trigger(FORM_FIELDS.MIDDLE_NAME);
        },
        [setValue, trigger, getValues, dispatch],
    );

    const showAgreement = useMemo(() => ![PAGE.AGENTS].includes(page), [page]);
    const showGosuslugiAuth = !user && isMainPage;

    const errorsInNameOrLastName = Boolean(errors[FORM_FIELDS.FIRST_NAME] || errors[FORM_FIELDS.LAST_NAME]);

    const noPatronymicChecked = useMemo(() => {
        if (errorsInNameOrLastName) {
            return false;
        }
        return patronymic === null;
    }, [patronymic, errorsInNameOrLastName]);

    return (
        <>
            <Row>
                <Col>
                    <Heading level={4}>Контактные данные</Heading>
                </Col>
            </Row>
            {showGosuslugiAuth && <GosuslugiAuth />}

            <Row>
                <Col>
                    <Controller
                        control={control}
                        name={FORM_FIELDS.FULL_NAME}
                        render={({ field: { onBlur, ...field } }) => (
                            <FullNameSuggest
                                input={{
                                    ...field,
                                    label: LABELS[FORM_FIELDS.FULL_NAME],
                                    errorMessage:
                                        errors[FORM_FIELDS.LAST_NAME]?.message ||
                                        errors[FORM_FIELDS.FIRST_NAME]?.message ||
                                        errors[FORM_FIELDS.MIDDLE_NAME]?.message,
                                }}
                                onChange={field.onChange}
                                onSelect={onFullNameSelect}
                                onBlur={onBlur}
                            />
                        )}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Checkbox
                        checked={noPatronymicChecked}
                        onChange={onChangeNoPatronymic}
                        disabled={errorsInNameOrLastName}>
                        Отчество отсутствует
                    </Checkbox>
                </Col>
            </Row>
            <Row>
                <Col md-4>
                    <Controller
                        control={control}
                        name={FORM_FIELDS.EMAIL}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                type="email"
                                label={LABELS[FORM_FIELDS.EMAIL]}
                                errorMessage={errors[FORM_FIELDS.EMAIL]?.message as string}
                            />
                        )}
                    />
                </Col>
                <Col md-4>
                    <PhoneForm form={form} page={page} />
                </Col>
            </Row>
            {showAgreement && (
                <Row>
                    <Col>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.AGREEMENT}
                            defaultValue={true}
                            render={({ field: { value, ...field } }) => (
                                <AgreementLink
                                    errorMessage={errors[FORM_FIELDS.AGREEMENT]?.message}
                                    checked={value}
                                    {...field}
                                />
                            )}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};
